import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { PagedAndSortedRequestDto } from '@shared/components/paged-listing-component-base';
import { SportPagedResultDto } from './sport-paged-result-dto';
import { SportDto } from './sport-dto';
import { ServiceBase } from '../service-base';
import { String } from 'typescript-string-operations';
import {ApiRequest} from "@shared/models/api-request";
import { WaffleFeatureFlagsService } from '@shared/services/feature-flags/waffle-feature-flags.service';
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { ApiSecureService } from '../api/api-secure.service';
import { ApiBaseService } from '../api-base.service';
import { ConvertLimitsData } from '@shared/interfaces/convert-limits.interface';

@Injectable()
export class SportService extends ServiceBase {
  private readonly featureFlagService: WaffleFeatureFlagsService = inject(WaffleFeatureFlagsService);
  private readonly apiSecureService: ApiSecureService = inject(ApiSecureService);
  private readonly apiBaseService: ApiBaseService = inject(ApiBaseService);
  
  public list(request: PagedAndSortedRequestDto): Observable<SportPagedResultDto> {
    const apiUrl: string = this._appConstants.API.SPORT.LIST;
    const apiRequest: ApiRequest = this._apiService.prepareInputSortAndSearchPage(apiUrl, request);

    let apiCall;
    if (this.featureFlagService.getValue(FeatureFlagEnum.ENCRYPTION)) {
      apiCall = this.apiSecureService.secureGet<any>(apiRequest.apiUrl);
    } else {
      apiCall = this._apiService.get(apiRequest.apiUrl);
    }

    return apiCall.pipe(
      map((res: any) => {
        return this.mappingSportPageResultDto(res);
      })
    );
  }

  public getSportById(id: number): Observable<SportDto> {
    const apiUrl: string = String.Format(this._appConstants.API.SPORT.GET_DETAIL, id);

    return this._apiService
      .get(apiUrl)
      .pipe(map((item: Object) => new SportDto().deserialize(item)));
  }

  public createSport(input: unknown): Observable<SportDto> {
    const apiUrl: string = this._appConstants.API.SPORT.ADD;

    return this._apiService
      .post(apiUrl, input)
      .pipe(map((item) => new SportDto().deserialize(item)));
  }

  public updateSport(id: number, input: unknown): Observable<SportDto> {
    const apiUrl: string = String.Format(this._appConstants.API.SPORT.UPDATE, id);

    return this._apiService
      .put(apiUrl, input)
      .pipe(map((item: Object) => new SportDto().deserialize(item)));
  }

  public deleteSport(id: number): Observable<any> {
    const apiUrl: string = String.Format(this._appConstants.API.SPORT.DELETE, id);

    return this._apiService.delete(apiUrl);
  }

  public listAll(): Observable<SportPagedResultDto> {
    const apiUrl: string = this._appConstants.API.SPORT.LIST;

    let apiCall;
    if (this.featureFlagService.getValue(FeatureFlagEnum.ENCRYPTION)) {
      apiCall = this.apiSecureService.secureGet<any>(apiUrl);
    } else {
      apiCall = this._apiService.get(apiUrl);
    }

    return apiCall.pipe(
      map((res: any) => {
        return this.mappingSportPageResultDto(res);
      })
    );
  }

  public convertLimits(sportId: number, params: ConvertLimitsData): Observable<ConvertLimitsData> {
    const apiUrl: string = String.Format(this._appConstants.API.SPORT.CONVERT_LIMITS, sportId);
    return this.apiBaseService.postJson(apiUrl, params);
  }

  public batchConvertLimits(sportId: number, params: Array<ConvertLimitsData>): Observable<Array<ConvertLimitsData>> {
    const apiUrl: string = String.Format(this._appConstants.API.SPORT.BATCH_CONVERT_LIMITS, sportId);
    return this.apiBaseService.postJson(apiUrl, params);
  }

  private mappingSportPageResultDto(data: any): SportPagedResultDto {
    return new SportPagedResultDto().deserialize({
      totalCount: data.count,
      items: data.results.map((item: SportDto) =>
        new SportDto().deserialize(item)
      ),
    });
  }
}
